// src/pages/SevenAmLibrary.jsx
// 7AM Library — Biblioteca de audios educativos ejecutivos
// Pública, sin autenticación requerida.

import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowRight, Headphones } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURACIÓN DE ESTADOS
// Para habilitar un episodio: cambiar 'locked' por 'active'
// ─────────────────────────────────────────────────────────────────────────────
const EPISODE_STATES = {
  0:  'active',
  1:  'active',
  2:  'active',
  3:  'locked',
  4:  'locked',
  5:  'locked',
  6:  'locked',
  7:  'locked',
  8:  'locked',
  9:  'locked',
  10: 'locked',
};

// ─────────────────────────────────────────────────────────────────────────────
// CATÁLOGO DE EPISODIOS
// ─────────────────────────────────────────────────────────────────────────────
const EPISODES = [
  {
    id: 0,
    title: 'El cerebro que decide sin avisarte',
    subtitle: 'Cómo el pensamiento automático gobierna las decisiones ejecutivas sin que nadie lo note',
    book: 'Pensar rápido, pensar despacio',
    author: 'Daniel Kahneman',
    cover: '/assets/library/covers/ep-0.png',
    url: '/7am-library/episodio-0',
    accentBg: '#EFF6FF',
    accentBorder: '#BFDBFE',
  },
  {
    id: 1,
    title: 'El error que se repite no es mala suerte',
    subtitle: 'Por qué las crisis recurrentes de tu organización son el resultado exacto de cómo está diseñada su estructura',
    book: 'Thinking in Systems',
    author: 'Donella Meadows',
    cover: '/assets/library/covers/ep-1.png',
    url: '/7am-library/episodio-1',
    accentBg: '#F0FDF4',
    accentBorder: '#BBF7D0',
  },
  {
    id: 2,
    title: 'El ruido invisible de las decisiones',
    subtitle: 'Por qué tus juicios son más inconsistentes de lo que crees — y qué le cuesta eso a tu organización',
    book: 'Noise',
    author: 'Kahneman, Sibony, Sunstein',
    cover: '/assets/library/covers/ep-2.jpg',
    url: '/7am-library/episodio-2',
    accentBg: '#F5F3FF',
    accentBorder: '#DDD6FE',
  },
  {
    id: 3,
    title: 'No juzgues la decisión por el resultado',
    subtitle: 'Cómo separar la calidad de una decisión de su desenlace — y por qué confundirlos es el error más costoso del juicio ejecutivo',
    book: 'Thinking in Bets',
    author: 'Annie Duke',
    cover: '/assets/library/covers/ep-3.jpg',
    url: '/7am-library/episodio-3',
    accentBg: '#FFF7ED',
    accentBorder: '#FED7AA',
  },
  {
    id: 4,
    title: 'Predecir mejor no es talento — es método',
    subtitle: 'Por qué algunos decisores anticipan con más precisión que otros — y cómo se entrena esa disciplina',
    book: 'Superforecasting',
    author: 'Tetlock & Gardner',
    cover: '/assets/library/covers/ep-4.jpg',
    url: '/7am-library/episodio-4',
    accentBg: '#EFF6FF',
    accentBorder: '#BFDBFE',
  },
  {
    id: 5,
    title: 'Lo que no mides no lo gestionas — y sí puedes medirlo',
    subtitle: 'Por qué lo que llamas intangible suele ser algo que no has intentado medir con el método correcto',
    book: 'How to Measure Anything',
    author: 'Douglas Hubbard',
    cover: '/assets/library/covers/ep-5.jpg',
    url: '/7am-library/episodio-5',
    accentBg: '#ECFDF5',
    accentBorder: '#A7F3D0',
  },
  {
    id: 6,
    title: 'Tu plan estratégico no es una estrategia',
    subtitle: 'Por qué la mayoría de los planes son listas de deseos con presupuesto — y qué le falta para ser estrategia real',
    book: 'Good Strategy Bad Strategy',
    author: 'Richard Rumelt',
    cover: '/assets/library/covers/ep-6.jpg',
    url: '/7am-library/episodio-6',
    accentBg: '#FFF1F2',
    accentBorder: '#FECDD3',
  },
  {
    id: 7,
    title: 'El éxito de hoy bloquea el futuro',
    subtitle: 'Por qué las organizaciones exitosas toman decisiones correctas que las llevan al fracaso de mañana',
    book: "The Innovator's Dilemma",
    author: 'Clayton Christensen',
    cover: '/assets/library/covers/ep-7.jpg',
    url: '/7am-library/episodio-7',
    accentBg: '#FFF7ED',
    accentBorder: '#FED7AA',
  },
  {
    id: 8,
    title: 'No estás resolviendo el problema real del cliente',
    subtitle: 'Por qué las organizaciones diseñan soluciones para el trabajo equivocado — y cómo identificar el trabajo real',
    book: 'Competing Against Luck',
    author: 'Christensen & Dillon',
    cover: '/assets/library/covers/ep-8.png',
    url: '/7am-library/episodio-8',
    accentBg: '#F5F3FF',
    accentBorder: '#DDD6FE',
  },
  {
    id: 9,
    title: 'Tu organización no aprende — reacciona',
    subtitle: 'Por qué el conocimiento individual no se convierte en aprendizaje organizacional sin arquitectura',
    book: 'The Fifth Discipline',
    author: 'Peter Senge',
    cover: '/assets/library/covers/ep-9.jpg',
    url: '/7am-library/episodio-9',
    accentBg: '#ECFDF5',
    accentBorder: '#A7F3D0',
  },
  {
    id: 10,
    title: 'El criterio que nadie escucha',
    subtitle: 'Por qué sin seguridad psicológica los mejores juicios se quedan callados — aunque el talento esté en la sala',
    book: 'The Fearless Organization',
    author: 'Amy Edmondson',
    cover: '/assets/library/covers/ep-10.jpg',
    url: '/7am-library/episodio-10',
    accentBg: '#FFF1F2',
    accentBorder: '#FECDD3',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// TARJETA DE EPISODIO
// Mobile: layout horizontal (imagen izquierda, contenido derecha)
// md+:    layout vertical (imagen arriba, contenido abajo)
// ─────────────────────────────────────────────────────────────────────────────
function EpisodeCard({ episode }) {
  const isActive = EPISODE_STATES[episode.id] === 'active';

  return (
    <article
      className="bg-white rounded-xl overflow-hidden transition-all duration-200"
      style={{ border: '0.5px solid #E5E7EB', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
    >
      {/* items-stretch: en mobile la columna imagen se estira hasta la altura del contenido */}
      <div className="flex flex-row md:flex-col items-stretch">

        {/* Portada del libro
            Mobile:  w-28 fijo, altura = stretch al contenido (flex)
            Desktop: w-full + aspect-[2/3] → ratio correcto sin importar tamaño original */}
        <div
          className="relative flex-shrink-0 w-28 sm:w-32 md:w-full overflow-hidden md:aspect-[2/3]"
          style={{ backgroundColor: episode.accentBg, minHeight: '140px' }}
        >
          <img
            src={episode.cover}
            alt={`Portada ${episode.book}`}
            className="absolute inset-0 w-full h-full object-cover object-center"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {/* Badge número de episodio */}
          <div className="absolute top-2 left-2 z-10">
            <span
              className="text-[9px] font-semibold tracking-widest px-1.5 py-0.5 rounded uppercase"
              style={{ backgroundColor: 'rgba(255,255,255,0.88)', color: '#374151' }}
            >
              EP. {episode.id}
            </span>
          </div>
          {/* Overlay bloqueado */}
          {!isActive && (
            <div className="absolute inset-0 z-10 flex items-center justify-center"
              style={{ backgroundColor: 'rgba(17,24,39,0.38)' }}>
              <Lock className="w-5 h-5 text-white/80" strokeWidth={1.5} />
            </div>
          )}
        </div>

        {/* Contenido textual */}
        <div className="flex-1 p-3 md:p-4 flex flex-col justify-between min-w-0">
          <div>
            {/* Número episodio */}
            <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 font-medium">
              Episodio {episode.id}
            </p>
            {/* Título conceptual */}
            <h3
              className="font-semibold leading-snug text-[#0c203b] mb-1.5 line-clamp-3 md:line-clamp-none"
              style={{ fontSize: '15px' }}
            >
              {episode.title}
            </h3>
            {/* Subtítulo */}
            <p
              className="text-gray-500 leading-relaxed mb-2 line-clamp-2 md:line-clamp-4"
              style={{ fontSize: '12px' }}
            >
              {episode.subtitle}
            </p>
            {/* Referencia bibliográfica */}
            <p
              className="text-gray-400 italic truncate"
              style={{ fontSize: '11px' }}
            >
              {episode.book} — {episode.author}
            </p>
          </div>

          {/* Botón de acción */}
          <div className="mt-3">
            {isActive ? (
              <Link
                to={episode.url}
                className="flex items-center justify-center gap-1.5 w-full py-2.5 rounded-lg font-medium transition-all duration-200
                  border border-[#0c203b] text-[#0c203b] hover:bg-[#0c203b] hover:text-white"
                style={{ fontSize: '12px' }}
              >
                Escuchar episodio
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            ) : (
              <div
                className="flex items-center justify-center gap-1.5 w-full py-2.5 rounded-lg select-none"
                style={{
                  fontSize: '12px',
                  cursor: 'default',
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  color: '#9CA3AF',
                }}
              >
                <Lock className="w-3 h-3" strokeWidth={2} />
                Próximamente
              </div>
            )}
          </div>
        </div>

      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PÁGINA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function SevenAmLibrary() {
  const activeCount = Object.values(EPISODE_STATES).filter((s) => s === 'active').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="7AM Library | Edutech Pulse Lab — Criterio ejecutivo destilado"
        description="Once episodios. Once libros de referencia. El conocimiento ejecutivo destilado en audio de alta densidad. Acceso libre, sin registro."
        keywords="audios ejecutivos, formación ejecutiva, decisiones, liderazgo, toma de decisiones, kahneman, estrategia"
        canonicalUrl="/7am-library"
      />

      <Navbar />

      {/* ── HERO ── */}
      <header className="bg-[#0c203b] text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 pb-9 sm:pt-14 sm:pb-12">

          <p className="text-[10px] text-gray-400 uppercase tracking-[0.18em] mb-3 font-medium">
            by Edutech Pulse Lab
          </p>

          {/* Titular con logo inline en desktop, apilado en móvil */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
            <img
              src="/assets/library/Logos/7AM_Library.png"
              alt="7AM Library"
              className="h-7 sm:h-10 object-contain self-start sm:self-auto flex-shrink-0"
            />
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight tracking-tight">
              Criterio ejecutivo destilado
            </h1>
          </div>

          {/* Descripción */}
          <p className="text-gray-300 text-sm sm:text-base leading-relaxed max-w-xl mb-5 font-light">
            Once libros de referencia. El conocimiento que tarda años en acumularse, 
            destilado en audio de alta densidad para decisores activos.
          </p>

          {/* Stats */}
          <div className="flex items-center gap-2 text-[11px] text-gray-500">
            <Headphones className="w-3.5 h-3.5 text-gray-500" strokeWidth={1.5} />
            <span>{EPISODES.length} episodios</span>
            <span className="w-px h-3 bg-gray-600 mx-1" />
            <span>{activeCount} disponible{activeCount !== 1 ? 's' : ''}</span>
            <span className="w-px h-3 bg-gray-600 mx-1" />
            <span>Acceso libre</span>
          </div>

        </div>
      </header>

      {/* ── GRILLA DE EPISODIOS ── */}
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {EPISODES.map((episode) => (
            <EpisodeCard key={episode.id} episode={episode} />
          ))}
        </div>
      </main>

      {/* ── FOOTER NOTA ── */}
      <footer className="max-w-5xl mx-auto px-4 sm:px-8 pb-12 pt-2">
        <p className="text-[11px] text-gray-400 text-center">
          Los episodios bloqueados se habilitan progresivamente. Acceso libre, sin registro requerido.
        </p>
      </footer>

    </div>
  );
}
